'use client'

import { SSRContext } from '@urql/next'
import { createStore } from '@xstate/store'
import { Suspense, useContext, useMemo } from 'react'
import { SSRData } from 'urql'

const hydrationStatusStoreCache = new Map<string, ReturnType<typeof createHydrationStatusStore>>()

/**
 * Creates a store to track the hydration status of SSR data
 *
 * Uses xstate store to maintain state across component suspense renders,
 * which prevents infinite render loops that would occur with regular React state
 * since suspense unmounts the component when a promise is thrown
 *
 * @returns A store with hydration status tracking
 */
function createHydrationStatusStore() {
  return createStore({
    context: {
      status: 'pending' as 'pending' | 'success' | 'error'
    },
    on: {
      setStatus: (_, event: { status: 'pending' | 'success' | 'error' }) => {
        return { status: event.status }
      }
    }
  })
}

/**
 * Gets or creates a persistent hydration status store for a specific data payload
 *
 * Uses a Map cache to ensure we track the state of each unique data payload
 * across component remounts during suspense
 *
 * @param key - Stringified version of the SSR data to use as cache key
 * @returns The hydration status store for the specified data
 */
function getHydrationStatusStore(key: string) {
  if (hydrationStatusStoreCache.has(key)) {
    return hydrationStatusStoreCache.get(key)
  }

  const store = createHydrationStatusStore()

  hydrationStatusStoreCache.set(key, store)
  return store
}

/**
 * Inner component that handles the actual data hydration and suspense logic
 *
 * This component:
 * 1. Accesses the SSR exchange from context
 * 2. Creates/retrieves a hydration status store for the specific data payload
 * 3. Suspends with a promise until the data is hydrated
 * 4. Throws an error if the SSR exchange isn't available
 *
 * @param extractedData - The SSR data to be rehydrated into the urql client
 */
function UrqlHydrationInner({ extractedData }: { extractedData: SSRData }) {
  const ssrExchange = useContext(SSRContext)
  const statusStore = getHydrationStatusStore(JSON.stringify(extractedData))

  const hydrationPromise = useMemo(() => {
    if (statusStore?.getSnapshot().context.status === 'success') return null
    return new Promise<void>((resolve) => {
      queueMicrotask(() => {
        if (ssrExchange) {
          ssrExchange.restoreData(extractedData)
          statusStore?.send({ type: 'setStatus', status: 'success' })
        } else {
          statusStore?.send({ type: 'setStatus', status: 'error' })
        }
        resolve()
      })
    })
  }, [extractedData, ssrExchange, statusStore])

  if (statusStore?.getSnapshot().context.status === 'pending') {
    throw hydrationPromise
  } else if (statusStore?.getSnapshot().context.status === 'success') {
    return null
  } else {
    throw new Error('SSR exchange not found')
  }
}

/**
 * Boundary component that hydrates urql client data during client-side navigation
 *
 * Unlike @next/urql's standard SSR hydration that only works on initial page load,
 * this component manually rehydrates data fetched in server components during
 * client-side navigation.
 *
 * Uses Suspense to ensure downstream components don't attempt to fetch data until
 * the urql client has been properly hydrated with server data.
 *
 * @param extractedData - The SSR data to be rehydrated into the urql client
 * @param children - Child components that will render after hydration is complete
 * @param fallback - Optional fallback UI during hydration (defaults to "Loading cache...")
 */
export function UrqlHydrationBoundary({
  extractedData,
  children,
  fallback = <div>Loading cache...</div>
}: {
  extractedData: SSRData
  children: React.ReactNode
  fallback?: React.ReactNode
}) {
  return (
    <Suspense fallback={fallback}>
      <UrqlHydrationInner extractedData={extractedData} />
      {children}
    </Suspense>
  )
}
